import { useLanguage } from '../contexts/LanguageContext';
import { useEffect, useRef, useCallback, useState } from 'react';

const PageBackground = ({ children, bgImage }) => {
  const transformRef = useRef(null);
  const overlayRef = useRef(null);
  const fadeRef = useRef(null);
  const [layers, setLayers] = useState([bgImage]);
  const prevImageRef = useRef(bgImage);

  // Crossfade when bgImage changes
  useEffect(() => {
    if (bgImage && bgImage !== prevImageRef.current) {
      prevImageRef.current = bgImage;
      setLayers(prev => [...prev, bgImage]);

      // Remove old layers after transition
      const timer = setTimeout(() => {
        setLayers([bgImage]);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [bgImage]);

  // Trigger fade-in animation for new layer via double rAF
  useEffect(() => {
    if (fadeRef.current && layers.length > 1) {
      const el = fadeRef.current;
      el.style.opacity = '0';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (el) el.style.opacity = '1';
        });
      });
    }
  }, [layers]);

  const handleScroll = useCallback(() => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

    // If the page barely scrolls (< 300px), keep background completely static
    if (maxScroll < 300) {
      if (transformRef.current) transformRef.current.style.transform = 'none';
      if (overlayRef.current) overlayRef.current.style.background = 'rgba(5, 12, 10, 0.93)';
      return;
    }

    const progress = Math.min(window.scrollY / maxScroll, 1);

    if (transformRef.current) {
      const scale = 1 + progress * 2.0;
      const translateY = progress * -35;
      transformRef.current.style.transform = `scale(${scale}) translateY(${translateY}%)`;
    }
    if (overlayRef.current) {
      const opacity = 0.93 + progress * 0.06;
      const r = Math.round(5 - progress * 2);
      const g = Math.round(12 - progress * 4);
      const b = Math.round(10 - progress * 3);
      overlayRef.current.style.background = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    // Re-evaluate on resize or content changes
    const ro = new ResizeObserver(() => handleScroll());
    ro.observe(document.documentElement);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, [handleScroll]);

  return (
    <div className="min-h-screen relative bg-[#0a0f0d]">
      <div className="fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div
          ref={transformRef}
          className="absolute inset-0 will-change-transform"
          style={{ transformOrigin: 'center 70%' }}
        >
          {layers.map((src, i) => {
            const isNew = i === layers.length - 1 && layers.length > 1;
            return (
              <div
                key={src}
                ref={isNew ? fadeRef : null}
                className="absolute inset-0"
                style={{
                  backgroundImage: `url('${src}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center 30%',
                  transition: isNew ? 'opacity 1s ease' : 'none',
                }}
              />
            );
          })}
        </div>
        <div ref={overlayRef} className="absolute inset-0" />
      </div>
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default PageBackground;
